/**
 * Main App Component with Routing
 * Sets up React Router and main application layout
 */

import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Loader from '@amzn/meridian/loader';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import { useAppSelector, useAppDispatch } from '../store/store';
import { getUser, fetchUserMetadata } from '../store/userSlice';
import { fetchAllConfig } from '../store/configSlice';
import { useReportPluginVersion } from '../utils/autofillVersion';
import authService from '../services/authService';
import { hasLength } from '../utils/dataTransformations';
import { withCondition } from '../utils/withCondition';
import Header from './shared/header/Header';
import SiteToaster from './shared/siteToaster/SiteToaster';
import SimulationBannerComponent from './shared/simulation/SimulationBanner';
import { simulatedLdapToBannerTitle } from './shared/simulation/dataTransformations';
import ModalProviderComponent from './modal/ModalProvider';
import SessionsList from './pages/sessionsList/SessionsList';
import SessionDetailPage from './pages/sessionDetail/SessionDetailPage';
import ResourcesPage from './pages/resources/ResourcesPage';

const ProfileManagementPage = React.lazy(() => import('./pages/profiles/ProfileManagementPage'));
const SiteMembersPage = React.lazy(() => import('./pages/siteMembers/SiteMembersPage'));
const MetricsPage = React.lazy(() => import('./pages/metrics/MetricsPage'));
const SessionMergePageComponent = React.lazy(() => import('./pages/sessionMerge/SessionMergePage'));

const SimulationBannerWithCondition = withCondition(SimulationBannerComponent);

const guardFallback = <Row width="fill" alignmentHorizontal="center"><Loader size="large" /></Row>;

/** Route guard for admin pages (engineer OR admin group — matches backend require_admin) */
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAdmin = useAppSelector((state) => state.user.isAdmin);

  if (!isAdmin) {
    return <Navigate to="/session/new" replace />;
  }

  return <Suspense fallback={guardFallback}>{children}</Suspense>;
};

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token, userLoading, simulatedLdap } = useAppSelector((state) => state.user);
  const SimulationBanner = SimulationBannerWithCondition(hasLength(simulatedLdap));
  const simulationTitle = simulatedLdapToBannerTitle(simulatedLdap);

  // Detect the installed autofill userscript and report version changes to the backend.
  useReportPluginVersion();

  // Initialize user authentication and config on mount
  useEffect(() => {
    if (userLoading === 'idle') {
      dispatch(getUser());
    }
  }, [dispatch, userLoading]);

  // Fetch configuration and the user's site memberships from backend after auth
  useEffect(() => {
    if (token) {
      dispatch(fetchAllConfig());
      dispatch(fetchUserMetadata());
    }
  }, [dispatch, token]);

  // Periodic token refresh — keeps localStorage token fresh for API calls and WebSocket connections.
  // Cognito SDK uses the refresh token (3-day TTL) to silently get new ID/access tokens (12h TTL).
  // Without this, the cached token in localStorage would go stale and cause "Token has expired" errors
  // on subsequent API calls or WebSocket reconnections.
  // Runs every 50 minutes (well within the 12h ID token TTL).
  useEffect(() => {
    if (!token) return;

    const TOKEN_REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes

    const refreshInterval = setInterval(async () => {
      console.log('Periodic token refresh triggered');
      await authService.refreshToken();
    }, TOKEN_REFRESH_INTERVAL_MS);

    return () => clearInterval(refreshInterval);
  }, [token]);

  // Show loader while authenticating
  if (userLoading === 'pending') {
    return (
      <Row width="fill" alignmentHorizontal="center">
        <Column height="80vh" alignmentVertical="center">
          <Loader size="large" />
        </Column>
      </Row>
    );
  }

  // Require authentication
  if (!token) {
    return (
      <Row width="fill" alignmentHorizontal="center">
        <Column height="80vh" alignmentVertical="center" spacing="300">
          <h1>WHSNavigator</h1>
          <p>Please log in to continue</p>
          <Loader />
        </Column>
      </Row>
    );
  }

  return (
    <BrowserRouter>
      <ModalProviderComponent>
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Header />
          <SimulationBanner title={simulationTitle} />
          <SiteToaster />
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Routes>
              {/* Default: New Session */}
              <Route path="/" element={<Navigate to="/session/new" replace />} />

              {/* Session History */}
              <Route path="/sessions" element={<SessionsList />} />

              {/* Compare and merge two sessions (admin only) */}
              <Route path="/sessions/merge" element={<AdminRoute><SessionMergePageComponent /></AdminRoute>} />

              {/* Session Detail (also handles /session/new for new recordings) */}
              <Route path="/session/:sessionId" element={<SessionDetailPage />} />

              {/* Profile Management (admin only — requires workingwell-engineer LDAP group) */}
              <Route path="/profiles" element={<AdminRoute><ProfileManagementPage /></AdminRoute>} />

              <Route path="/resources" element={<ResourcesPage />} />

              {/* Site member management (admin only) */}
              <Route path="/site-members" element={<AdminRoute><SiteMembersPage /></AdminRoute>} />

              {/* Business metrics dashboard (admin only — the API is admin-gated too) */}
              <Route path="/metrics" element={<AdminRoute><MetricsPage /></AdminRoute>} />

              {/* Catch-all redirect to new session */}
              <Route path="*" element={<Navigate to="/session/new" replace />} />
            </Routes>
          </div>
        </div>
      </ModalProviderComponent>
    </BrowserRouter>
  );
};

export default App;
